import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import UpcomingEventCard from '../components/UpcomingEventCard';
import { Clock, Calendar, ListTodo, BarChart3, Users, Play, ArrowRight, ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';

const HomePage = () => {
  const { user } = useAuth();
  const { isRunning, isPaused, formatTime, elapsedTime } = useTimer();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const quickAccessCards = [
    {
      title: t('timer.title'),
      description: t('dashboard.startTimerDesc'),
      icon: Clock,
      path: '/timer',
      gradient: 'from-[#FEE2E2] to-[#FECACA]',
      iconBg: 'bg-[#D93025]',
      iconColor: 'text-white',
      isActive: isRunning
    },
    {
      title: t('nav.plan'),
      description: t('plan.subtitle'),
      icon: ClipboardList,
      path: '/plan',
      gradient: 'from-[#FEF3C7] to-[#FDE68A]',
      iconBg: 'bg-[#B45309]',
      iconColor: 'text-white'
    },
    {
      title: t('office.title'),
      description: t('office.subtitle'),
      icon: Calendar,
      path: '/office',
      gradient: 'from-[#E0F2FE] to-[#BAE6FD]',
      iconBg: 'bg-[#0369A1]',
      iconColor: 'text-white'
    },
    {
      title: t('tasks.title'),
      description: t('tasks.subtitle'),
      icon: ListTodo,
      path: '/tasks',
      gradient: 'from-[#F3E8FF] to-[#E9D5FF]',
      iconBg: 'bg-[#7E22CE]',
      iconColor: 'text-white'
    },
    {
      title: t('weekly.title'),
      description: t('weekly.infoText'),
      icon: BarChart3,
      path: '/weekly',
      gradient: 'from-[#D1FAE5] to-[#A7F3D0]',
      iconBg: 'bg-[#047857]',
      iconColor: 'text-white'
    }
  ];

  if (user?.role === 'admin') {
    quickAccessCards.push({
      title: t('admin.title'),
      description: t('admin.subtitle'),
      icon: Users,
      path: '/admin',
      gradient: 'from-[#FEF3C7] to-[#FDE68A]',
      iconBg: 'bg-[#B45309]',
      iconColor: 'text-white'
    });
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="home-page">
        {/* Welcome Section */}
        <div className="text-center mb-10">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('dashboard.welcome', { name: user?.name })}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-base text-zinc-600"
          >
            {t('dashboard.subtitle')}
          </motion.p>
        </div>

        {/* Active Timer Banner */}
        {isRunning && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 bg-gradient-to-r from-[#D93025] to-[#B91C1C] p-4 sm:p-6 md:p-8 rounded-3xl shadow-2xl text-white overflow-hidden"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0"
                >
                  <Play className="w-6 h-6 sm:w-8 sm:h-8" fill="white" />
                </motion.div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-0.5 sm:mb-1">{isPaused ? t('timer.paused') : t('timer.running')}</h3>
                  <p className="text-white/80 text-sm sm:text-base">{t('dashboard.timerActiveDesc')}</p>
                </div>
              </div>
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-mono font-bold flex-shrink-0" style={{ fontFamily: 'Space Mono, monospace' }}>
                {formatTime(elapsedTime)}
              </div>
            </div>
          </motion.div>
        )}

        {/* Upcoming Event */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <UpcomingEventCard />
        </motion.div>

        {/* Quick Access Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quickAccessCards.map((card, index) => (
            <motion.div
              key={card.path}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => navigate(card.path)}
              className={`group relative bg-gradient-to-br ${card.gradient} p-8 rounded-3xl cursor-pointer 
                hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden border-2 border-white/50`}
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12" />
              </div>

              {/* Content */}
              <div className="relative z-10">
                <div className={`${card.iconBg} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 
                  group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 shadow-lg`}>
                  <card.icon className={`w-8 h-8 ${card.iconColor}`} />
                </div>

                <h3 className="text-2xl font-bold text-zinc-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {card.title}
                </h3>
                
                <p className="text-zinc-700 mb-4 line-clamp-2">
                  {card.description}
                </p>

                <div className="flex items-center text-zinc-900 font-medium group-hover:gap-3 gap-2 transition-all">
                  <span>{t('common.open')}</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>

                {card.isActive && (
                  <div className="absolute top-4 right-4">
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="w-3 h-3 bg-[#D93025] rounded-full shadow-lg"
                    />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-white p-8 rounded-3xl border-2 border-zinc-200"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#E0F2FE] rounded-xl flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-[#0369A1]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-zinc-900 mb-2">Asunya Management</h3>
              <p className="text-zinc-600">
                Verwalte deine Arbeitszeit, plane Büro-Tage, organisiere Aufgaben und behalte den Überblick über deine Woche. 
                Alles an einem Ort, einfach und übersichtlich.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default HomePage;
